import React, { useState, useMemo } from 'react';
import { Box, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Pagination, CircularProgress, IconButton, Tooltip, alpha, useTheme } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import type { GridColDef } from '@mui/x-data-grid';
import AddchartRoundedIcon from '@mui/icons-material/AddchartRounded';
import CloudDownload from '@mui/icons-material/CloudDownload';
import { buildApiUrl, tenantApiPrefix } from '../config';
import { getTenantIdFromStorage } from '../utils/onboardingLocalStorage';
import { downloadWorkflowDataCSV } from '../utils/csvExportV2';
import { SnackbarContext } from '../ui/SnackbarProvider';

// Inlined from pages/v2/types/chat.ts — kept local to avoid a shared-types dependency
interface TableResult {
  data: {
    frame_id: string;
    columns: string[];
    row_count: number;
    preview_rows: any[][];
    is_complete: boolean;
  };
  displayHint?: 'table' | 'list';
  eventId?: string;
}

interface TableResultBlockProps {
  tableResult: TableResult;
  workflowId?: string;
  threadId?: string;
  onAddToDashboard?: (workflowId: string, eventId: string, title: string, widgetType?: string) => void;
  fillContainer?: boolean;
}

// Inlined from pages/v2/constants/layout.ts
const ASSISTANT_MESSAGE_WIDTH = '90%';

const ACRONYM_MAP: Record<string, string> = {
  'emv': 'eMV',
  'vin': 'VIN',
  'mv': 'MV',
};

function toTitleCase(str: string): string {
  if (str.includes(' ') || /[A-Z]/.test(str)) {
    return str;
  }
  return str
    .toLowerCase()
    .split('_')
    .map(word => {
      if (ACRONYM_MAP[word]) {
        return ACRONYM_MAP[word];
      }
      for (const [key, value] of Object.entries(ACRONYM_MAP)) {
        if (word.startsWith(key)) {
          const rest = word.slice(key.length);
          if (rest && (/^\d/.test(rest) || /^-\d/.test(rest))) {
            return value + rest;
          } else if (rest) {
            return value + rest.charAt(0).toUpperCase() + rest.slice(1);
          }
          return value;
        }
      }
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
}

function formatCellValue(value: any): string {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (typeof value === 'number') {
    if (Number.isInteger(value) && value >= 1900 && value <= 2100) {
      return String(value);
    }
    if (Number.isInteger(value)) {
      return value.toLocaleString();
    }
    return value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  }
  if (typeof value === 'string') {
    const isoDateRegex = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2})?/;
    if (isoDateRegex.test(value)) {
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          const hasTime = value.includes('T') && !value.includes('T00:00:00');
          if (hasTime) {
            return date.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
          }
          return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
        }
      } catch {
        // fall through
      }
    }
    const uuidPattern = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[0-9a-f]{32,}/gi;
    let cleaned = value.replace(uuidPattern, '');
    cleaned = cleaned.replace(/[,\s]+$/, '').replace(/^[,\s]+/, '').replace(/,\s*,/g, ',').replace(/\s+/g, ' ').trim();
    if (cleaned !== value) {
      return cleaned;
    }
  }
  return String(value);
}

function truncateText(text: string, maxLength: number = 50): { display: string; isTruncated: boolean; full: string } {
  const str = String(text);
  if (str.length <= maxLength) {
    return { display: str, isTruncated: false, full: str };
  }
  return { display: str.substring(0, maxLength) + '...', isTruncated: true, full: str };
}

function isNumericColumn(rows: any[][], colIdx: number): boolean {
  const sample = rows.slice(0, 20);
  if (sample.length === 0) return false;
  const numericCount = sample.filter((r) => typeof r[colIdx] === 'number').length;
  return numericCount / sample.length > 0.7;
}

export function TableResultBlock({ tableResult, workflowId, threadId, onAddToDashboard, fillContainer = false }: TableResultBlockProps) {
  const { data, displayHint, eventId } = tableResult;
  const { notify } = React.useContext(SnackbarContext);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const numericCols = useMemo(() => {
    const set = new Set<string>();
    data.columns.forEach((col, idx) => {
      if (isNumericColumn(data.preview_rows, idx)) set.add(col);
    });
    return set;
  }, [data.columns, data.preview_rows]);

  const gridColumns: GridColDef[] = useMemo(
    () =>
      data.columns.map((col) => {
        const isNum = numericCols.has(col);
        return {
          field: col,
          headerName: toTitleCase(col),
          flex: 1,
          minWidth: 100,
          sortable: true,
          headerAlign: isNum ? ('right' as const) : ('left' as const),
          align: isNum ? ('right' as const) : ('left' as const),
          renderCell: (params: any) => {
            const val = params.value;
            const formatted = formatCellValue(val);
            const { display, isTruncated, full } = truncateText(formatted, 60);
            if (isTruncated) {
              return (
                <Tooltip title={full} arrow placement="top">
                  <span style={{ cursor: 'help', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{display}</span>
                </Tooltip>
              );
            }
            return <span>{display}</span>;
          },
        };
      }),
    [data.columns, numericCols],
  );

  const gridRows = useMemo(
    () =>
      data.preview_rows.map((row, idx) => {
        const obj: Record<string, any> = { id: idx };
        data.columns.forEach((col, ci) => { obj[col] = row[ci] ?? null; });
        return obj;
      }),
    [data.preview_rows, data.columns],
  );

  const PAGE_SIZE = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [displayRows, setDisplayRows] = useState(data.preview_rows.slice(0, PAGE_SIZE));
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const totalPages = Math.ceil(data.row_count / PAGE_SIZE);
  const showPagination = data.row_count > PAGE_SIZE;

  const handleDownload = async () => {
    if (!workflowId) {
      notify('Workflow ID not available', 'error');
      return;
    }
    setDownloading(true);
    try {
      const result = await downloadWorkflowDataCSV(workflowId, data.frame_id);
      notify(`Downloaded ${result.filename}`, 'success');
    } catch (error: any) {
      if (error.message.includes('No dataset found')) {
        notify('No dataset available', 'info');
      } else if (error.message.includes('no longer available')) {
        notify('Data expired. Please re-run the query.', 'warning');
      } else {
        notify(error.message || 'Failed to export data', 'error');
      }
    } finally {
      setDownloading(false);
    }
  };

  const fetchPage = async (page: number) => {
    if (page <= 5) {
      const startIdx = (page - 1) * PAGE_SIZE;
      setDisplayRows(data.preview_rows.slice(startIdx, startIdx + PAGE_SIZE));
      setCurrentPage(page);
      return;
    }
    if (!threadId) {
      console.error('Cannot fetch page: threadId not provided');
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('klaris_jwt');
      const tid = getTenantIdFromStorage();
      if (!tid) throw new Error('Not authenticated');
      const url = buildApiUrl(`${tenantApiPrefix(tid)}/frame-data/${data.frame_id}/rows?page=${page}&page_size=${PAGE_SIZE}&thread_id=${threadId}`);
      const response = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!response.ok) throw new Error(`Failed to fetch page: ${response.statusText}`);
      const result = await response.json();
      setDisplayRows(result.rows);
      setCurrentPage(page);
    } catch (error) {
      console.error('Failed to fetch page:', error);
    } finally {
      setLoading(false);
    }
  };

  if (fillContainer) {
    return (
      <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box sx={{ height: 3, flexShrink: 0, bgcolor: 'primary.main', opacity: 0.7 }} />
        <DataGrid
          rows={gridRows}
          columns={gridColumns}
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          density="compact"
          disableRowSelectionOnClick
          sx={{
            border: 0, borderRadius: 0, fontSize: '0.78rem', flex: 1,
            '& .MuiDataGrid-columnHeaders': {
              bgcolor: isDark ? alpha(theme.palette.common.white, 0.04) : alpha(theme.palette.primary.main, 0.04),
              borderBottom: '1px solid', borderColor: 'divider', minHeight: '36px !important', maxHeight: '36px !important',
            },
            '& .MuiDataGrid-columnHeader': { '&:focus, &:focus-within': { outline: 'none' } },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 700, fontSize: '0.68rem', letterSpacing: '0.06em', textTransform: 'uppercase',
              color: isDark ? alpha(theme.palette.common.white, 0.55) : alpha(theme.palette.common.black, 0.5),
            },
            '& .MuiDataGrid-columnSeparator': {
              color: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.10)',
              '&:hover': { color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.35)' },
            },
            '& .MuiDataGrid-cell': { borderColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)', color: 'text.primary', fontSize: '0.78rem', '&:focus, &:focus-within': { outline: 'none' } },
            '& .MuiDataGrid-row': {
              '&:nth-of-type(even)': { bgcolor: isDark ? alpha(theme.palette.common.white, 0.018) : alpha(theme.palette.common.black, 0.018) },
              '&:hover': { bgcolor: isDark ? alpha(theme.palette.primary.main, 0.07) : alpha(theme.palette.primary.main, 0.04) },
            },
            '& .MuiDataGrid-footerContainer': { borderTop: '1px solid', borderColor: 'divider', minHeight: 38, bgcolor: isDark ? alpha(theme.palette.common.white, 0.02) : alpha(theme.palette.common.black, 0.015) },
            '& .MuiTablePagination-root, & .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.71rem', color: 'text.secondary' },
            '& .MuiDataGrid-overlay': { bgcolor: 'transparent' },
          }}
        />
      </Box>
    );
  }

  return (
    <Paper elevation={0} sx={{ width: ASSISTANT_MESSAGE_WIDTH, maxWidth: ASSISTANT_MESSAGE_WIDTH, height: 'auto', overflow: 'hidden', border: '1px solid', borderColor: 'divider', borderRadius: '12px', position: 'relative', display: 'block' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.75rem' }}>
          {displayHint === 'list' ? 'List' : 'Results'} ({data.row_count} rows)
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {workflowId && eventId && onAddToDashboard && (
            <Button size="small" variant="contained" startIcon={<AddchartRoundedIcon fontSize="small" />}
              onClick={() => onAddToDashboard(workflowId, eventId, 'Table Results', 'table_result')}
              sx={{ px: 1.5, py: 0.5, fontSize: '0.75rem', textTransform: 'none', fontWeight: 500, backgroundColor: 'primary.main', color: 'white', '&:hover': { backgroundColor: 'primary.dark' } }}>
              Pin to Dashboard
            </Button>
          )}
          <Tooltip title="Download full dataset as CSV" arrow>
            <IconButton size="small" onClick={handleDownload} disabled={downloading || !workflowId}
              sx={{ color: 'primary.main', '&:hover': { backgroundColor: 'primary.light', color: 'primary.dark' }, '&.Mui-disabled': { color: 'action.disabled' } }}>
              <CloudDownload fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <TableContainer sx={{ maxHeight: 400, overflow: 'auto' }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {data.columns.map((col, i) => (
                <TableCell key={i} sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>{toTitleCase(col)}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={data.columns.length} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
            ) : (
              displayRows.map((row, i) => (
                <TableRow key={i} hover>
                  {row.map((cell, j) => {
                    const { display, isTruncated, full } = truncateText(formatCellValue(cell), 50);
                    return (
                      <TableCell key={j} sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {isTruncated ? <Tooltip title={full} arrow placement="top"><span style={{ cursor: 'help' }}>{display}</span></Tooltip> : <span>{display}</span>}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {showPagination && (
        <Box sx={{ p: 2, bgcolor: 'grey.50', borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Showing {(currentPage - 1) * PAGE_SIZE + 1}-{Math.min(currentPage * PAGE_SIZE, data.row_count)} of {data.row_count.toLocaleString()} rows
          </Typography>
          <Pagination count={totalPages} page={currentPage} onChange={(e, page) => fetchPage(page)} disabled={loading} color="primary" size="small" showFirstButton showLastButton />
        </Box>
      )}
    </Paper>
  );
}
